import Link from "next/link";
import {
	type ButtonHTMLAttributes,
	type ComponentPropsWithoutRef,
	type HTMLAttributes,
	type ReactNode,
	type Ref,
	forwardRef,
} from "react";

import { cn } from "../utils";

type InlineLinkChipTone = "default" | "negative";

type SharedProps = {
	children?: ReactNode;
	tone?: InlineLinkChipTone;
	selected?: boolean;
	interactive?: boolean;
	className?: string;
};

type InlineLinkChipLinkProps = SharedProps &
	Omit<ComponentPropsWithoutRef<typeof Link>, "className" | "children"> & {
		href: string;
		isButton?: false;
	};

type InlineLinkChipButtonProps = SharedProps &
	Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "children"> & {
		isButton: true;
		href?: never;
	};

type InlineLinkChipSpanProps = SharedProps &
	Omit<HTMLAttributes<HTMLSpanElement>, "className" | "children"> & {
		isButton?: false;
		href?: never;
	};

export type InlineLinkChipProps =
	| InlineLinkChipLinkProps
	| InlineLinkChipButtonProps
	| InlineLinkChipSpanProps;

const getToneClasses = (tone: InlineLinkChipTone, interactive: boolean) => {
	if (tone === "negative") {
		return cn(
			"bg-palette-negative-3 border-palette-negative-6 text-palette-negative-11",
			interactive && "hover:bg-palette-negative-4 cursor-pointer"
		);
	}

	return cn(
		"bg-palette-gray-3 border-palette-gray-6 text-primary",
		interactive && "hover:bg-palette-gray-4 cursor-pointer"
	);
};

export const inlineLinkChipClassName = ({
	tone = "default",
	selected = false,
	interactive = false,
	className,
}: SharedProps) =>
	cn(
		"inline-flex items-center gap-1 rounded border px-1 text-sm font-normal transition-colors select-none",
		"no-underline hover:no-underline focus:no-underline active:no-underline",
		getToneClasses(tone, interactive),
		selected && "ring-1 ring-brand border-brand",
		className
	);

export const InlineLinkChip = forwardRef<HTMLElement, InlineLinkChipProps>(
	function InlineLinkChip(props, ref) {
		const {
			children,
			tone = "default",
			selected = false,
			interactive = false,
			className,
		} = props;
		const chipClassName = inlineLinkChipClassName({
			tone,
			selected,
			interactive,
			className,
		});

		if ("href" in props && props.href) {
			const {
				href,
				children: _,
				tone: __,
				selected: ___,
				interactive: ____,
				className: _____,
				...restProps
			} = props;

			return (
				<Link
					{...(restProps as Omit<InlineLinkChipLinkProps, keyof SharedProps | "href">)}
					href={href}
					ref={ref as Ref<HTMLAnchorElement>}
					className={chipClassName}
				>
					{children}
				</Link>
			);
		}

		if (props.isButton) {
			const {
				children: _,
				tone: __,
				selected: ___,
				interactive: ____,
				className: _____,
				isButton: ______,
				...restProps
			} = props;

			return (
				<button
					{...(restProps as Omit<
						InlineLinkChipButtonProps,
						keyof SharedProps | "isButton"
					>)}
					ref={ref as Ref<HTMLButtonElement>}
					className={chipClassName}
				>
					{children}
				</button>
			);
		}

		const {
			children: _,
			tone: __,
			selected: ___,
			interactive: ____,
			className: _____,
			...restProps
		} = props;

		return (
			<span
				{...(restProps as Omit<InlineLinkChipSpanProps, keyof SharedProps>)}
				ref={ref as Ref<HTMLSpanElement>}
				className={chipClassName}
			>
				{children}
			</span>
		);
	}
);
InlineLinkChip.displayName = "InlineLinkChip";
